import { LEGAL_PATROCINADOR_CALIENTE, LEGAL_PATROCINADOR_CODERE } from "../constantes/constantes";

/**
 * Formatea una fecha en formato de 24 horas (hh:mm), asegurando dos dígitos.
 * 
 * @param {string|number|Date} time - Fecha o timestamp válido.
 * @returns {string} Hora formateada en formato "HH:MM".
 */
export const hourFormat = time => {
  const date = new Date(time);
  if (isNaN(date.getTime())) {
    return '--:--';
  }
  return new Intl.DateTimeFormat('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(date);
}

/**
 * Formatea una fecha al estilo español: `DD Mes, YYYY`.
 * Ejemplo: `5 Julio, 2024`.
 * 
 * @param {string|number|Date} time - Fecha o timestamp válido.
 * @returns {string} Fecha formateada.
 */
export const dateFormatDDmesAnio = time => {
  const date = new Date(time);
  if (isNaN(date.getTime())) {
    return 'Fecha inválida';
  }
  const formatter = new Intl.DateTimeFormat('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  const parts = formatter.formatToParts(date);
  const day = parts.find(p => p.type === 'day').value;
  const month = parts.find(p => p.type === 'month').value
    .replace(/^\w/, c => c.toUpperCase());
  const year = parts.find(p => p.type === 'year').value;

  return `${day} ${month}, ${year}`;
}

/**
 * Genera un identificador de sesión aleatorio.
 * Ejemplo de salida: _5gkf93jkd
 * 
 * @returns {string} ID de sesión pseudoaleatorio.
 */
export const generateSessionId = () => {
  return `_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Extrae el primer bloque JSON válido del texto
 */
function extractFirstJsonBlock(text) {
  const s = String(text ?? "").trim();
  const start = s.search(/[\{\[]/);
  if (start === -1) return null;

  const opener = s[start];
  const closer = opener === "{" ? "}" : "]";
  let inString = false;
  let escaped = false;
  let depth = 0;

  for (let i = start; i < s.length; i++) {
    const c = s[i];

    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (c === "\\") {
        escaped = true;
      } else if (c === '"') {
        inString = false;
      }
      continue;
    }

    if (c === '"') {
      inString = true;
      continue;
    }

    if (c === opener) depth++;
    if (c === closer) depth--;

    if (depth === 0) {
      return s.slice(start, i + 1);
    }
  }

  return null;
}


/**
 * Limpia y parsea un objeto que contiene JSON envuelto en markdown.
 * Usa jsonrepair para reparar JSON malformado.
 * 
 * @param {Array|Object} response - Respuesta que puede contener JSON en formato markdown.
 * @returns {Object|null} Objeto JSON parseado o null si hay error.
 */
export const cleanJsonResponse = (response) => {
  try {
    const data = Array.isArray(response) ? response[0] : response;
    const textContent = data?.text || data;
    
    if (typeof textContent !== "string") {
      console.warn('[cleanJsonResponse] Texto no es string válido');
      return null;
    }

    // Limpiar markdown
    const cleaned = textContent
      .replace(/```json\s*/g, "")
      .replace(/```\s*/g, "")
      .trim();

    // Extraer el primer bloque JSON
    const extracted = extractFirstJsonBlock(cleaned) ?? cleaned;

    // Intento 1: Parse directo
    try {
      const parsed = JSON.parse(extracted);
      return parsed;
    } catch (e) {
      console.warn('[cleanJsonResponse] falló:', e.message);
    }

  } catch (error) {
    console.error('[cleanJsonResponse] Error general:', error);
    return null;
  }
};


export function stripAccentsLower(s = "") {
  return String(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

// Genera:
// - norm: texto sin acentos y en minúsculas
// - map: map[normIndex] = originalIndex
export function buildNormalizedIndexMap(original = "") {
  let norm = "";
  const map = [];

  for (let i = 0; i < original.length; i++) {
    const ch = original[i];

    // Normaliza el char (puede producir 1 o más chars, ej: "á" -> "a")
    const normalized = stripAccentsLower(ch);

    // Agrega cada char normalizado y mapea su índice al índice original
    for (let k = 0; k < normalized.length; k++) {
      norm += normalized[k];
      map.push(i);
    }
  }

  return { norm, map };
}

export function tokenizeQueryAccents(query = "") {
  return stripAccentsLower(query)
    .split(/\s+/)
    .map(t => t.trim())
    .filter(t => t.length >= 2);
}

export function findRanges(normText, tokens) {
  const ranges = [];

  // tokens más largos primero para mejores highlights
  const ordered = [...tokens].sort((a, b) => b.length - a.length);

  for (const token of ordered) {
    let start = 0;

    while (start < normText.length) {
      const idx = normText.indexOf(token, start);
      if (idx === -1) break;

      const end = idx + token.length;

      // Evitar solapamientos
      const overlaps = ranges.some(r => !(end <= r.start || idx >= r.end));
      if (!overlaps) {
        ranges.push({ start: idx, end });
      }

      start = end; // siguiente búsqueda
    }
  }

  // ordenar por inicio
  ranges.sort((a, b) => a.start - b.start);
  return ranges;
}


/**
 * Parsea la respuesta de search_games extrayendo y validando el JSON embebido
 * 
 * @description La respuesta viene como un objeto con propiedad 'text' que contiene
 * un string JSON. Extrae y parsea el JSON del texto.
 * 
 * @param {Object} result - Objeto con propiedad text
 * @param {string} result.text - Texto con JSON embebido
 * 
 * @returns {Object|null} Objeto JSON parseado con estructura:
 *   - query: término de búsqueda
 *   - filter_clauses: cláusulas de filtro
 *   O null si hay error en el parsing
 * 
 * @example
 * const gamesData = parseSearchGamesResponse(result);
 * if (gamesData?.query) {
 *   console.log(`Query: ${gamesData.query}`);
 * }
 */
export const parseSearchGamesResponse = (result) => {
  try {
    // Validar que result existe y tiene propiedad text
    if (!result || typeof result !== 'object') {
      console.warn('[parseSearchGamesResponse] Result no es un objeto válido:', result);
      return null;
    }

    let responseText = result.text || '';
    
    if (!responseText || typeof responseText !== 'string') {
      console.warn('[parseSearchGamesResponse] No hay texto en la respuesta:', result);
      return null;
    }

    // Limpiar espacios en blanco y saltos de línea
    responseText = responseText.trim();
    
    // Intento 1: Parsear directamente si es JSON válido
    try {
      const gamesData = JSON.parse(responseText);
      return gamesData;
    } catch (e) {
      console.warn('[parseSearchGamesResponse] Parse directo falló, intentando extraer JSON embebido');
    }

    // Intento 2: Extraer JSON del texto (buscar entre ``` json y ```)
    const jsonMatch = responseText.match(/```json\n([\s\S]*?)\n```/);
    
    if (jsonMatch && jsonMatch[1]) {
      const gamesData = JSON.parse(jsonMatch[1]);
      return gamesData;
    }

    // Intento 3: Buscar cualquier bloque JSON entre { }
    const jsonBlockMatch = responseText.match(/\{[\s\S]*\}/);
    if (jsonBlockMatch) {
      const gamesData = JSON.parse(jsonBlockMatch[0]);
      console.log('[parseSearchGamesResponse] Datos parseados desde bloque { }:', gamesData);
      return gamesData;
    }

    console.warn('[parseSearchGamesResponse] No se encontró JSON válido en la respuesta');
    return null;
  } catch (parseErr) {
    console.error('[parseSearchGamesResponse] Error parseando respuesta:', parseErr);
    return null;
  }
};

/**
 * Devuelve el texto legal correspondiente al patrocinador.
 * Si no se encuentra, retorna string vacío.
 */
export function getLegalTextBySponsor(patrocinador = '') {
  const legalTextMap = {
    codere: LEGAL_PATROCINADOR_CODERE,
    caliente: LEGAL_PATROCINADOR_CALIENTE,
    strendus: 'Permiso SEGOB DGG/SP/404/97. Diviértete responsablemente. Los juegos de apuestas están prohibidos para menores de edad.'
  };

  return legalTextMap[patrocinador?.trim().toLowerCase()] || '';
}

/**
 * Determina la clase del patrocinador basada en el valor proporcionado
 * @param {string} patrocinador - Nombre del patrocinador
 * @returns {string} Clase CSS correspondiente al patrocinador
 */
export const getSponsorClass = (patrocinador) => {
  switch (patrocinador) {
    case 'caliente': return 'sponsor-caliente';
    case 'codere': return 'sponsor-codere';
    case 'strendus': return 'sponsor-strendus';
    default: return '';
  }
};

// Formatea fecha en formato "ABR 9 | 18:45" (24 horas)
export function formatResultsDate(dateString) {
  const date = new Date(dateString);
  const month = date.toLocaleString('es-MX', { month: 'short' }).toUpperCase(); // ABR
  const day = date.getDate(); // 1 - 31

  let hours = date.getHours(); // 0 - 23 (ya en 24h)
  const minutes = date.getMinutes();
  const minuteStr = minutes.toString().padStart(2, '0'); // 8 -> 08
  const hourStr = hours.toString().padStart(2, '0'); // 9 -> 09

  return `${month} ${day} | ${hourStr}:${minuteStr}`; // Ejemplo: ABR 3 | 18:45
}

/**
 * Acción común para botones patrocinados en cards.
 * Abre la URL en una nueva pestaña sin propagar el evento.
 */
export function handleClickCard(e, url) {
  e.preventDefault();
  e.stopPropagation();
  if (url) window.open(url, '_blank');
};

/**
 * Retorna la clase final para el marcador dependiendo del estado del juego y el equipo.
 */
export const getScoreClass = (baseClass, teamClass, classGame) => {
  if (classGame === 'clarosports-error-game') return `${baseClass} score-error`;

  if (teamClass === 'clarosports-winner' && classGame !== 'clarosports-next-game' && classGame !== 'clarosports-error-game') {
    return classGame === 'clarosports-finished-game' ? `${baseClass} score-ganador-final` : `${baseClass} score-ganador`;
  }
  if (teamClass === 'clarosports-draw' && classGame === 'clarosports-finished-game') {
    return `${baseClass} score-empate`;
  }
  if (teamClass === 'clarosports-draw' && (classGame === 'clarosports-next-game' || classGame === 'clarosports-in-progress-game')) {
    return `${baseClass} score-empate`;
  }
  if (teamClass === 'clarosports-loser' || (classGame === 'clarosports-next-game' && teamClass === 'clarosports-winner')) {
    return `${baseClass} score-perdedor`;
  }

  return baseClass;
};